'use client';

/**
 * Widget content picker — the HOST-side dialog behind `host.content.pick`.
 *
 * Security model: the picker is portal chrome rendered OVER the canvas, never
 * inside the widget iframe. The widget only learns about the items the user
 * explicitly picks here (which the bridge then records as read-grants) — the
 * `<input type="file">` capability-grant model applied to the user's library.
 *
 * Opened via a window CustomEvent('widget-content-pick') whose detail carries
 * { accept?, multi?, kinds?, resolve } — the bridge awaits `resolve(items)`.
 * Cancel / Esc resolves null.
 *
 * The dialog itself is the shared {@link PortalFileDialog} in `open` mode —
 * the SAME browser (breadcrumbs from "My files", folders + files, search,
 * upload, URL tab) that `host.documents.saveDialog` shows in `save` mode.
 */

import { useEffect, useState } from 'react';
import { PortalFileDialog, type PickedItem } from '@/components/workspace/portal-file-dialog';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';

export type { PickedItem };

interface PickRequest {
  accept?: string[];
  multi?: boolean;
  kinds?: ('file' | 'url')[];
  resolve: (items: PickedItem[] | null) => void;
}

export function WidgetContentPicker() {
  const [req, setReq] = useState<PickRequest | null>(null);
  // Close hands focus back to the app that asked (app-frame-focus-return.ts).
  const captureAppFocus = useAppFrameFocusReturn(!!req);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<PickRequest>).detail;
      if (!detail || typeof detail.resolve !== 'function') return;
      captureAppFocus();
      setReq(detail);
    };
    window.addEventListener('widget-content-pick', onOpen);
    return () => window.removeEventListener('widget-content-pick', onOpen);
  }, [captureAppFocus]);

  if (!req) return null;

  return (
    <PortalFileDialog
      mode="open"
      accept={req.accept}
      multi={req.multi}
      kinds={req.kinds}
      onResolve={(items) => {
        req.resolve(items);
        setReq(null);
      }}
    />
  );
}
